import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router} from '@angular/router';
import { delay } from 'rxjs/operators';
import { EventManager, JhiLanguageService, AlertService, ParseLinks, DateUtils } from 'ng-jhipster';

import { School, SchoolService } from '../school';
import { Lesson, LessonWithContextDetails, LessonStatus, LessonAvailability } from './lesson.model';
import { LessonService, LessonUtilService } from '.';
import { StudentUtilService } from '../../entities/student';
import { Reservation, ReservationStatus } from '../reservation/reservation.model';
import { CardService, Card } from '../card';
import { StudentCommentService } from '../student-comment';
import { ReservationService } from '../reservation/reservation.service';
import { ReservationUtilService } from '../reservation/reservation.util.service';
import { Principal } from '../../shared';
import { StudentService } from '../../entities/student/student.service';
import { Student } from '../student/student.model';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { NgbTabChangeEvent, NgbTabset } from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'jhi-lesson-detail',
    templateUrl: './lesson-detail.component.html'
})
export class LessonDetailComponent implements OnInit, OnDestroy {
    school: School;
    lesson: LessonWithContextDetails;
    lessonAvailability: LessonAvailability;
    reservations: Reservation[];
    reservationsCount: number;
    studentCommentsLoaded: boolean;
    totalNewReservations: any;
    totalCancelledReservations: any;
    totalAttendedReservations: any;
    totalNoShowReservations: any;
    reservationsPredicate: any;
    reservationsPerLesson: any;
    reservationSort: any;
    error: any;
    success: any;
    reverse: any;
    currencyCode: string = 'EUR'; // Euro ISO 4217 code
    currentUserIsStudent: boolean;
    currentStudent: Student;
    currentStudentReservation: Reservation;
    isProcessingReservationForCurrentUser: boolean;

    private subscription: any;
    private reservationSubscription: any;
    private lessonCancelledSubscription: any;
    private lessonExecutedSubscription: any;
    private lessonModifiedSubscription: any;
    private previousPage: String;

    public checkoutURLStatus: string;
    public comesFromSuccessCheckoutSession: boolean;
    public comesFromCancelCheckoutSession: boolean;
    private readonly CHECKOUT_SUCCESS = 'success';
    private readonly CHECKOUT_CANCEL = 'cancel';
    private retryCount = 0;
    private MAX_RETRY = 3;
    private MAX_TIME_DIFF = 15; // threshold in seconds;

    // variables for the tabs
    @ViewChild('tabs')
    tabSet: NgbTabset;
    currentTab: string;

    // variables to handle comparisons of reservation status
    readonly RESERVATION_STATUS_CANCELLED: any = 'CANCELLED';
    readonly RESERVATION_STATUS_NEW: any = 'NEW';
    readonly RESERVATION_STATUS_ATTENDED: any = 'ATTENDED';
    readonly RESERVATION_STATUS_NO_SHOW: any = 'NO_SHOW';

    constructor(
        private jhiLanguageService: JhiLanguageService,
        private lessonService: LessonService,
        private reservationService: ReservationService,
        private studentCommentService: StudentCommentService,
        private alertService: AlertService,
        private route: ActivatedRoute,
        private parseLinks: ParseLinks,
        private eventManager: EventManager,
        private principal: Principal,
        private studentUtilService: StudentUtilService,
        private studentService: StudentService,
        private router: Router,
        private reservationUtilService: ReservationUtilService,
        private schoolService: SchoolService,
        private cardService: CardService,
        private dateUtils: DateUtils
    ) {
        this.jhiLanguageService.setLocations(['lesson', 'lessonStatus', 'reservation', 'reservationStatus',
            'student', 'classType', 'user-management']);

        // TODO change this value, it shows only 200 reservations per lesson in the lesson detail page.
        this.reservationsPerLesson = 200;
        this.reservationsPredicate = 'id';
        this.reservationSort = 'desc';
    }

    ngOnInit() {
        this.comesFromSuccessCheckoutSession = false;
        this.comesFromCancelCheckoutSession = false;
        this.totalNewReservations = 0;
        this.totalAttendedReservations = 0;
        this.totalNoShowReservations = 0;
        this.totalCancelledReservations = 0;

        this.principal
        .identity()
        .then(() => {
                this.loadSchool();

                this.principal
                .hasOnlyAuthority('ROLE_USER')
                .then((response) => this.currentUserIsStudent = response);

                this.studentService.getCurrent().subscribe((student: any) => {
                    this.currentStudent = student;

                    this.subscription = this.route.params.subscribe(params => {
                        this.load(params['id']);
                        this.checkoutURLStatus = this.route.snapshot.queryParams['checkout']
                        ?  this.route.snapshot.queryParams['checkout']
                        : null;
                        this.previousPage = params['previousPage'];
                    });
                });

                this.reservationSubscription = this.eventManager
                    .subscribe('reservationListModification',
                    (response) => this.loadReservationList(this.lesson.id));

                this.lessonCancelledSubscription = this.eventManager
                    .subscribe('lessonCancelled',
                    (response) => {
                        this.lesson = response['content']['content'];
                        this.loadReservationList(this.lesson.id);
                    });

                this.lessonExecutedSubscription = this.eventManager
                    .subscribe('lessonExecuted',
                    (response) => this.lesson = response['content']['content']);

                this.lessonModifiedSubscription = this.eventManager
                    .subscribe('lessonListModification',
                    (response) => this.lesson = response['content']['content']);
            });
    }

    loadSchool() {
        this.schoolService.findCurrent()
                          .subscribe( (s: School) => {
                            this.school = s;
                          }, (error: any) => this.onError(error));
    }

    load(id: number) {
        this.lessonService.find(id).subscribe(lesson => {
            this.lesson = LessonWithContextDetails.fromLesson(lesson);
            this.loadCurrentStudentCards();
            this.loadCurrentStudentReservation();
        });
    }

    isLoadingReservations() {
        return this.reservationsCount > 0 && !this.reservations && !this.studentCommentsLoaded;
    }

    loadReservationList(id: number) {
        // we reset the reservations and reservations count to allow the loader to show
        // see isLoadingReservations method
        this.reservationsCount = 0;
        this.reservations = null;
        this.studentCommentsLoaded = false;

        this.reservationService.count({
            query: 'lesson.id:' + id
        })
        .pipe(delay(250))
        .subscribe(
            (res: number) => {
                this.reservationsCount = res;
                if (this.reservationsCount > 0 ) {
                    this.lessonService.getReservations(id).subscribe(
                        (response: HttpResponse<Reservation[]>) => this.onReservationsLoadSuccess(response.body, response.headers),
                        (response: HttpErrorResponse) => this.onError(response)
                    );
                }
            },
            (res: HttpErrorResponse) => {
                this.onError(res);
            }
        );
    }

    loadCurrentStudentReservation() {
        if (!this.lesson || !this.currentStudent) {
            return;
        }

        this.comesFromCancelCheckoutSession = false;
        this.comesFromSuccessCheckoutSession = false;

        this.reservationService.count({
            query: 'lesson.id:' + this.lesson.id + ' AND student.id:' + this.currentStudent.id
        })
        .pipe(delay(250))
        .subscribe(
            (res: number) => {
                this.reservationsCount = res;
                if (this.reservationsCount > 0 ) {
                    this.reservationService
                        .search({
                            query: 'lesson.id:' + this.lesson.id + ' AND student.id:' + this.currentStudent.id
                        }).subscribe(
                            (response: HttpResponse<Reservation[]>) => {
                                this.currentStudentReservation = response.body[0];
                                this.handleReservationCheckoutMessage();
                        }, (error: HttpErrorResponse) => {
                            this.onError(error);
                        });

                } else {
                    // if there are no reservations and the user has not payed, we need to handle the checkout message
                    this.handleReservationCheckoutMessage();
                }
            },
            (res: HttpErrorResponse) => {
                this.onError(res);
            }
        );

    }

    loadCurrentStudentCards() {
        // We get the cards of the current student for the specified dates on the search
        if (this.lesson && this.currentStudent) {
            let lessonDate  = this.dateUtils.convertDateTimeFromServer(this.lesson.begining);
            let realMonth = lessonDate.getMonth() + 1;
            let monthTerm = realMonth < 10 ? '0' + realMonth : realMonth;
            let realDate = lessonDate.getDate();
            let dayTerm = realDate < 10 ? '0' + realDate : realDate;

            let lessonDateTerm = lessonDate.getFullYear() + '-' + monthTerm + '-' + dayTerm;
            let cardsClause = 'validSince:[ * TO ' + lessonDateTerm + ']';
            cardsClause += ' AND validUntil:[' + lessonDateTerm + ' TO *]';
            cardsClause += ' AND student.id:' + this.currentStudent.id;
            this.cardService.count({
                query: cardsClause
            })
            .subscribe(
                (cardsCount: number) => {
                    if (cardsCount > 0) {
                        this.cardService.search({
                            query: cardsClause,
                            sort: [],
                        })
                        .subscribe(
                            (res: HttpResponse<Card[]>) => {
                                let cards: Card [] = res.body;
                                cards.forEach(card => {
                                    if (lessonDate >= card.validSince
                                        && lessonDate <= card.validUntil) {
                                        this.lesson.currentStudentCompatibleCards.push(card);
                                    }
                                });
                            },
                            (res: HttpErrorResponse) => {
                                this.onError(res);
                            }
                        );
                    }
                },
                (res: HttpErrorResponse) => {
                    this.onError(res);
                }
            );
        }
    }

    previousState() {
        window.history.back();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.reservationSubscription.unsubscribe();
        this.lessonCancelledSubscription.unsubscribe();
        this.lessonExecutedSubscription.unsubscribe();
        this.lessonModifiedSubscription.unsubscribe();
    }

    onReservationsLoadSuccess(data, headers) {
        data.sort((r1, r2) => {
            // We sort the list of participants alphabetically
            if (r1.student.label.toLowerCase() > r2.student.label.toLowerCase()) {
                return 1;
            } else if (r1.student.label.toLowerCase() < r2.student.label.toLowerCase()) {
                return -1;
            } else {
                return 0;
            }
        });

        this.reservations = data;
        this.calculateStats();
    }

    private calculateStats() {

        this.totalNewReservations = this.reservations.filter(r => this.RESERVATION_STATUS_NEW === r.status).length;

        this.totalCancelledReservations = this.reservations.filter(r => this.RESERVATION_STATUS_CANCELLED === r.status).length;

        this.totalAttendedReservations = this.reservations.filter(r => this.RESERVATION_STATUS_ATTENDED === r.status).length;

        this.totalNoShowReservations = this.reservations.filter(r => this.RESERVATION_STATUS_NO_SHOW === r.status).length;
    }

    saveReservation(id: number, newStatus: any) {
        if (id !== undefined) {
            let copy: Reservation = Object.assign({},
                this.reservations.find(r => r.id === id));
            copy.status = newStatus;
            this.reservationService.update(copy, undefined)
                .subscribe((res: Reservation) =>
                    this.onReservationSaveSuccess(res),
                (res: HttpErrorResponse) => this.onReservationSaveError(res));
        }
    }

    handleBookButtonClick(): void {
        LessonUtilService.handleBookClick(this.lesson
            , this.cardService
            , () => this.createReservationForCurrentUser()
            , (error) => this.onError(error)
            , this.router
        );
    }

    createReservationForCurrentUser() {
        if (!this.currentStudent || !this.lesson) {
            return;
        }
        this.isProcessingReservationForCurrentUser = true;
        let copy: Reservation = this.currentStudentReservation ? Object.assign({}, this.currentStudentReservation) : null;

        if (!copy) {
            let reservation: Reservation = new Reservation();
            reservation.status = ReservationStatus.NEW;
            reservation.lesson = this.lesson;
            reservation.student = this.currentStudent;

            this.reservationService.create(reservation, undefined)
                .subscribe((res: Reservation) => {
                    this.currentStudentReservation = res;
                    this.isProcessingReservationForCurrentUser = false;
                },
                (res: HttpErrorResponse) => this.onError(res));
        } else {
            copy.status = ReservationStatus.NEW;
            this.reservationService.update(copy, undefined)
                .subscribe(
                    (res: Reservation) => {
                        this.currentStudentReservation = res;
                        this.isProcessingReservationForCurrentUser = false;
                    },
                    (res: HttpErrorResponse) => {
                        this.onError(res);
                    }
                );
        }
    }

    cancelReservationForCurrentUser() {
        if (!this.currentStudentReservation || !this.currentStudent || !this.lesson) {
            return;
        }

        this.isProcessingReservationForCurrentUser = true;
        let reservation: Reservation = this.currentStudentReservation;
        let copy: Reservation = reservation ? Object.assign({}, reservation) : null;
        if (copy) {
            copy.status = ReservationStatus.CANCELLED;
            this.reservationService.update(copy, undefined)
                .subscribe((res: Reservation) => {
                    this.currentStudentReservation = res;
                    this.isProcessingReservationForCurrentUser = false;
                },
                (res: HttpErrorResponse) => this.onError(res));
        }
    }

    updateLessonStatus(lesson: Lesson, newStatus: LessonStatus) {
        let copy: Lesson = Object.assign({}, lesson);

        copy.status = newStatus;
        this.lessonService.update(copy).subscribe((res: Lesson) =>
            this.onLessonSaveSuccess(res), (res: HttpErrorResponse) => this.onError(res));
    }

    updateLessonPublishStatus(published: boolean) {
        let copy: Lesson = Object.assign({}, this.lesson);

        copy.published = published;
        this.lessonService.update(copy).subscribe((res: Lesson) =>
            this.onLessonSaveSuccess(res), (res: HttpErrorResponse) => this.onError(res));
    }

    getAvailablePlaces() {
        let availablePlaces = this.lesson.maxParticipants;
        if (this.totalAttendedReservations) {
            availablePlaces = availablePlaces - this.totalAttendedReservations;
        }

        if (this.totalNewReservations) {
            availablePlaces = availablePlaces - this.totalNewReservations;
        }

        return availablePlaces > 0 ? availablePlaces : 0;
    }

    getStatusBadgeStyle(percentage: number): string {
        return this.studentUtilService.getStatusBadgeStyle(percentage);
    }

    getReservationStyle(reservation: Reservation): String {
        if (reservation) {
            return this.reservationUtilService.getReservationStyle(
                reservation.status.toString());
        }
        return 'text-info';
    }

    getClassTypeBadgeStyle() {
        return  {'color': LessonUtilService.getBackgroundColor(this.lesson)};
    }

    getLastCancellationDate(lesson: Lesson): Date {
        return this.lessonService.getLastCancellationDate(lesson);
    }

    // Remove the currency symbol if there is no active product
    // TODO: Remove once totalPaid and total reflects always amounts
    getCurrencyCode(student): string {
        return student.status.activeProduct ? this.currencyCode : '';
    }

    disableButton(buttonName: string): boolean {
        let reservation: Reservation = this.currentStudentReservation;
        let currentReservationStatus = reservation ? reservation.status : null;

        if (this.lesson.status as any !== 'OPEN' ) {
            return true;
        }

        if (currentReservationStatus as any === 'ATTENDED' || currentReservationStatus as any === 'NO_SHOW') {
                return true;
        }

        // TODO also check if the current school is accepting online payments
        if (buttonName === 'checkout') {
            if (!LessonUtilService.allowToPayOnline(this.school, this.lesson, this.currentStudent)) {
                return true;
            }

            return currentReservationStatus as any === 'NEW';
        }

        if (buttonName === 'book') {
            return currentReservationStatus as any === 'NEW';
        }

        if (buttonName === 'cancelBooking') {
            // do not allow to cancel if the reservation has been payed
            // the reinboursement use case has not been yet developed
            return !currentReservationStatus ||
                currentReservationStatus as any === 'CANCELLED' ||
                this.reservationIsPayedOnline(reservation);
        }

        if (this.isProcessingReservationForCurrentUser) {
            return true;
        }

        return true;
    }

    private reservationIsPayedOnline(reservation: Reservation): boolean {
        return this.reservationUtilService.isPayedOnLine(reservation);

    }

    showButton(buttonName: string) {
        if (buttonName === 'book' && (this.currentStudentReservation == null
                                  || this.currentStudentReservation.status === this.RESERVATION_STATUS_CANCELLED)) {
            return LessonUtilService.allowCurrentStudentToBook(this.currentUserIsStudent, this.lesson);
        }

        if (buttonName === 'checkout' && (this.currentStudentReservation == null
                                      || this.currentStudentReservation.status === this.RESERVATION_STATUS_CANCELLED)) {
            return LessonUtilService.allowCurrentStudentToCheckout(this.currentUserIsStudent, this.lesson);
        }

        if (buttonName === 'cancelBooking' && this.currentUserIsStudent
                                           && this.currentStudentReservation
                                           && this.currentStudentReservation.status === this.RESERVATION_STATUS_NEW) {

            return true;
        }

        return false;
    }

    public onSelectChange(event: NgbTabChangeEvent) {
        this.currentTab = event.nextId;
        this.loadTab(event.nextId);
    }

    loadTab(tabId: string) {
        switch (tabId) {
            case 'lesson-info-tab':
                this.load(this.lesson.id);
            break;
            case 'participants-tab':
                this.loadReservationList(this.lesson.id);
            break;
        }
    }

    private onReservationSaveSuccess(updatedReservation: Reservation) {
        // update the reservation on the list of reservations. avoid reloading the list for performance and UX purposes
        const reservation = this.reservations.find(res => res.id === updatedReservation.id);
        if (reservation) {
            reservation.status = updatedReservation.status;
        }
        this.calculateStats();
    }

    private onLessonSaveSuccess(data) {
        this.load(this.lesson.id);
    }

    private onReservationSaveError(error: HttpErrorResponse) {
        this.onError(error);
    }

    private onError(error: HttpErrorResponse) {
        this.isProcessingReservationForCurrentUser = false;
        this.alertService.error(error.error, null, null);
    }

    private onLessonCancelledSuccess() {
        this.alertService.error('jYoguiApp.reservation.error.006', null, null);
    }

    private handleReservationCheckoutMessage() {
        if (this.checkoutURLStatus === this.CHECKOUT_CANCEL ) {
            this.comesFromCancelCheckoutSession = true;
            return;
        }

        if (this.checkoutURLStatus === this.CHECKOUT_SUCCESS ) {
            if (this.currentStudentReservation && this.currentStudentReservation.backofficeSessionId != null) {
                this.comesFromSuccessCheckoutSession = true;
                return;
            }

            // at this point there is a probability that there is an ongoing checkout session, or a session
            // has been finished, the creation of the reservfation, can take some time
            if (this.retryCount < this.MAX_RETRY) {
                this.retryCount++;
                console.log('retrying to get reservation of current user, retry count: ' + this.retryCount);
                setTimeout(() => {
                    this.loadCurrentStudentReservation();
                    }, 500);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/app/entities/lesson/lesson-detail.component.ts